import { CachedHttpClient } from '../cached-http-client';
import { buildQueryString } from './query-builder';

const cachedHttpClient = new CachedHttpClient(
  process.env['REACT_APP_API_BASE_URL'] || 'http://localhost:3001/api'
);

// Типы данных для отчетов
export interface ReportTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  isPublic: boolean;
  createdAt: string;
  createdBy: string;
  config: {
    dataSource: string;
    filters: Record<string, any>;
    visualizations: Array<{
      type: 'chart' | 'table' | 'metric';
      config: Record<string, any>;
    }>;
    layout: {
      columns: number;
      rows: number;
      widgets: Array<{
        id: string;
        type: string;
        position: { x: number; y: number; w: number; h: number };
        config: Record<string, any>;
      }>;
    };
  };
  tags: string[];
  version: string;
  lastModified: string;
}

export interface ScheduledReport {
  id: string;
  name: string;
  description: string;
  templateId: string;
  frequency: 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';
  time: string;
  recipients: string[];
  format: 'pdf' | 'excel' | 'csv' | 'json';
  isActive: boolean;
  lastRun?: string;
  nextRun: string;
  createdAt: string;
  createdBy: string;
  filters: Record<string, any>;
  deliveryMethod: 'email' | 'webhook' | 'download';
  webhookUrl?: string;
  subject?: string;
  message?: string;
}

export interface ReportExecution {
  id: string;
  scheduledReportId?: string;
  templateId: string;
  status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';
  startedAt: string;
  completedAt?: string;
  duration?: number;
  error?: string;
  fileUrl?: string;
  fileSize?: number;
  recipients?: string[];
  format: string;
  filters: Record<string, any>;
  createdBy: string;
}

export interface ReportData {
  data: any[];
  metadata: {
    totalRows: number;
    generatedAt: string;
    filters: Record<string, any>;
    dataSource: string;
  };
  visualizations: Array<{
    type: string;
    data: any;
    config: Record<string, any>;
  }>;
}

export interface ReportFilters {
  dateRange?: {
    start: string;
    end: string;
  };
  eventTypes?: string[];
  statuses?: string[];
  locations?: string[];
  departments?: string[];
  roles?: string[];
  customFilters?: Record<string, any>;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

// API для работы с шаблонами отчетов
export const reportsTemplatesApi = {
  // Получить все шаблоны отчетов
  getTemplates: async (params?: {
    page?: number;
    limit?: number;
    category?: string;
    search?: string;
    isPublic?: boolean;
  }): Promise<PaginatedResponse<ReportTemplate>> => {
    const response = await cachedHttpClient.get(`/reports/templates${buildQueryString(params || {})}`);
    return response.data;
  },

  // Получить шаблон по ID
  getTemplate: async (id: string): Promise<ReportTemplate> => {
    const response = await cachedHttpClient.get(`/reports/templates/${id}`);
    return response.data;
  },

  // Создать новый шаблон
  createTemplate: async (template: Omit<ReportTemplate, 'id' | 'createdAt' | 'createdBy' | 'version' | 'lastModified'>): Promise<ReportTemplate> => {
    const response = await cachedHttpClient.post('/reports/templates', template);
    return response.data;
  },

  // Обновить шаблон
  updateTemplate: async (id: string, template: Partial<ReportTemplate>): Promise<ReportTemplate> => {
    const response = await cachedHttpClient.put(`/reports/templates/${id}`, template);
    return response.data;
  },

  // Удалить шаблон
  // DELETE живёт на второй BE-поверхности шаблонов (`documents/reports`), а не на
  // `reports/templates` — обе бьют в один ReportsService, роли совпадают. Дубль-поверхность
  // зафиксирована как SSOT-долг в docs/архитектура/route-existence-triage.md.
  deleteTemplate: async (id: string): Promise<void> => {
    await cachedHttpClient.delete(`/documents/reports/templates/${id}`);
  },

  // Клонировать шаблон
  cloneTemplate: async (id: string, name: string): Promise<ReportTemplate> => {
    const response = await cachedHttpClient.post(`/reports/templates/${id}/clone`, { name });
    return response.data;
  },

  // Экспортировать шаблон
  exportTemplate: async (id: string, format: 'json' | 'yaml' = 'json'): Promise<Blob> => {
    const response = await cachedHttpClient.get(`/reports/templates/${id}/export?format=${format}`, {
      responseType: 'blob'
    });
    return response.data;
  },

  // Импортировать шаблон
  importTemplate: async (file: File): Promise<ReportTemplate> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await cachedHttpClient.post('/reports/templates/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  }
};

// API для работы с запланированными отчетами
export const reportsSchedulerApi = {
  // Получить все запланированные отчеты
  // Бэкенд: GET /api/reports/scheduled. Маппинг: lastRunAt->lastRun, nextRunAt->nextRun, cron->time, isActive->isActive
  getScheduledReports: async (params?: {
    page?: number;
    limit?: number;
    templateId?: string;
    isActive?: boolean;
  }): Promise<{ data: ScheduledReport[]; pagination?: { page: number; limit: number; total: number; totalPages: number } }> => {
    const response = await cachedHttpClient.get(`/reports/scheduled${buildQueryString(params || {})}`);
    const { data: rawList, pagination } = response.data as {
      data: Array<{
        id: string;
        name?: string;
        description?: string;
        templateId: string;
        cron: string;
        frequency?: string;
        filters?: Record<string, any>;
        recipients?: string[];
        isActive: boolean;
        lastRunAt?: string;
        nextRunAt?: string;
        createdAt?: string;
        createdBy?: string;
        metadata?: Record<string, any>;
      }>;
      pagination?: { page: number; limit: number; total: number; totalPages: number };
    };
    const mapFrequency = (f?: string): ScheduledReport['frequency'] => {
      const v = (f || '').toLowerCase();
      if (['daily', 'weekly', 'monthly', 'quarterly', 'yearly'].includes(v)) return v as ScheduledReport['frequency'];
      if (v === 'hourly') return 'daily';
      return 'daily';
    };
    const data: ScheduledReport[] = (rawList || []).map((item) => {
      const lastRunAt = item.lastRunAt;
      const meta = item.metadata;
      return {
        id: item.id,
        name: item.name || '',
        description: item.description || '',
        templateId: item.templateId,
        frequency: mapFrequency(item.frequency || (meta && meta['frequency'])),
        time: item.cron || '00:00',
        recipients: item.recipients || [],
        format: (meta && (meta['format'] as ScheduledReport['format'])) || 'pdf',
        isActive: item.isActive,
        ...(lastRunAt != null ? { lastRun: lastRunAt } : {}),
        nextRun: item.nextRunAt || new Date().toISOString(),
        createdAt: item.createdAt || new Date().toISOString(),
        createdBy: item.createdBy || '',
        filters: item.filters || {},
        deliveryMethod: (meta && (meta['deliveryMethod'] as ScheduledReport['deliveryMethod'])) || 'email',
        webhookUrl: meta && meta['webhookUrl'],
        subject: meta && meta['subject'],
        message: meta && meta['message'],
      };
    });
    return pagination != null ? { data, pagination } : { data };
  },

  // Получить запланированный отчет по ID
  getScheduledReport: async (id: string): Promise<ScheduledReport> => {
    const response = await cachedHttpClient.get(`/reports/scheduled/${id}`);
    return response.data;
  },

  // Создать запланированный отчет
  createScheduledReport: async (report: Omit<ScheduledReport, 'id' | 'createdAt' | 'createdBy'>): Promise<ScheduledReport> => {
    const response = await cachedHttpClient.post('/reports/scheduled', report);
    return response.data;
  },

  // Обновить запланированный отчет
  updateScheduledReport: async (id: string, report: Partial<ScheduledReport>): Promise<ScheduledReport> => {
    const response = await cachedHttpClient.put(`/reports/scheduled/${id}`, report);
    return response.data;
  },

  // Удалить запланированный отчет
  deleteScheduledReport: async (id: string): Promise<void> => {
    await cachedHttpClient.delete(`/reports/scheduled/${id}`);
  },

  // Запустить отчет вручную
  runScheduledReport: async (id: string): Promise<ReportExecution> => {
    const response = await cachedHttpClient.post(`/reports/scheduled/${id}/run`);
    return response.data;
  },

  // Приостановить отчет
  pauseScheduledReport: async (id: string): Promise<void> => {
    await cachedHttpClient.post(`/reports/scheduled/${id}/pause`);
  },

  // Возобновить отчет
  resumeScheduledReport: async (id: string): Promise<void> => {
    await cachedHttpClient.post(`/reports/scheduled/${id}/resume`);
  },

  // Получить историю выполнения
  getExecutionHistory: async (id: string, params?: {
    page?: number;
    limit?: number;
    status?: string;
  }): Promise<PaginatedResponse<ReportExecution>> => {
    const response = await cachedHttpClient.get(`/reports/scheduled/${id}/executions${buildQueryString(params || {})}`);
    return response.data;
  }
};

// API для выполнения отчетов
export const reportsExecutionApi = {
  // Выполнить отчет
  executeReport: async (templateId: string, filters: ReportFilters, format: string = 'pdf'): Promise<ReportExecution> => {
    const response = await cachedHttpClient.post('/reports/execute', {
      templateId,
      filters,
      format
    });
    return response.data;
  },

  // Получить данные отчета
  getReportData: async (templateId: string, filters: ReportFilters): Promise<ReportData> => {
    const response = await cachedHttpClient.post('/reports/data', {
      templateId,
      filters
    });
    return response.data;
  },

  // Получить статус выполнения
  getExecutionStatus: async (executionId: string): Promise<ReportExecution> => {
    const response = await cachedHttpClient.get(`/reports/executions/${executionId}`);
    return response.data;
  },

  // Отменить выполнение
  cancelExecution: async (executionId: string): Promise<void> => {
    await cachedHttpClient.post(`/reports/executions/${executionId}/cancel`);
  },

  // Скачать отчет
  downloadReport: async (executionId: string): Promise<Blob> => {
    const response = await cachedHttpClient.get(`/reports/executions/${executionId}/download`, {
      responseType: 'blob'
    });
    return response.data;
  },

  // Получить все выполнения
  getExecutions: async (params?: {
    page?: number;
    limit?: number;
    status?: string;
    templateId?: string;
    dateFrom?: string;
    dateTo?: string;
  }): Promise<PaginatedResponse<ReportExecution>> => {
    const response = await cachedHttpClient.get(`/reports/executions${buildQueryString(params || {})}`);
    return response.data;
  }
};

// API для статистики отчетов
export const reportsStatsApi = {
  // Получить общую статистику
  getStats: async (): Promise<{
    totalTemplates: number;
    totalScheduledReports: number;
    totalExecutions: number;
    activeScheduledReports: number;
    failedExecutions: number;
    totalDataProcessed: number;
    averageExecutionTime: number;
    popularTemplates: Array<{
      id: string;
      name: string;
      executions: number;
    }>;
    recentExecutions: Array<{
      id: string;
      templateName: string;
      status: string;
      createdAt: string;
    }>;
  }> => {
    const response = await cachedHttpClient.get('/reports/stats');
    return response.data;
  },

  // Получить статистику по шаблонам
  getTemplateStats: async (templateId: string): Promise<{
    totalExecutions: number;
    successfulExecutions: number;
    failedExecutions: number;
    averageExecutionTime: number;
    lastExecution: string;
    dataVolume: number;
  }> => {
    const response = await cachedHttpClient.get(`/reports/templates/${templateId}/stats`);
    return response.data;
  },

  // Получить статистику по запланированным отчетам
  getScheduledStats: async (): Promise<{
    totalScheduled: number;
    activeScheduled: number;
    pausedScheduled: number;
    nextExecutions: Array<{
      id: string;
      name: string;
      nextRun: string;
    }>;
    executionHistory: Array<{
      date: string;
      executions: number;
      successful: number;
      failed: number;
    }>;
  }> => {
    const response = await cachedHttpClient.get('/reports/scheduled/stats');
    return response.data;
  }
};

// Экспорт всех API
const reportsApi = {
  templates: reportsTemplatesApi,
  scheduler: reportsSchedulerApi,
  execution: reportsExecutionApi,
  stats: reportsStatsApi
};
export default reportsApi;